import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, CalendarCheck, CheckCircle2, Loader2, ShieldCheck, Video } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["09:30", "11:00", "14:00", "16:00", "18:30"];

export default function DiagnosticBooking() {
    const [date, setDate] = useState();
    const [slot, setSlot] = useState();
    const [form, setForm] = useState({ name: "", email: "", phone: "", child_age: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!date || !slot) {
            toast.error("Choisissez une date et un créneau.");
            return;
        }
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Indiquez votre prénom et votre email.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/diagnostic/request`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                message:
                    `Âge enfant: ${form.child_age || "n/c"}. ` + (form.message.trim() || ""),
                date: format(date, "yyyy-MM-dd"),
                slot,
            });
            setDone(true);
            toast.success("Votre demande est envoyée — nous revenons vers vous sous 48h.");
        } catch {
            toast.error("Une erreur est survenue, merci de réessayer.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-ink-50/30">
            <header className="border-b border-ink-100 bg-white/85 backdrop-blur-xl sticky top-0 z-40">
                <div className="container-wide flex items-center justify-between h-24">
                    <Logo />
                    <Link to="/" className="btn-ghost text-sm" data-testid="diag-back-home">
                        <ArrowLeft className="mr-1.5 h-4 w-4" strokeWidth={2} /> Retour au site
                    </Link>
                </div>
            </header>

            <main className="container-wide py-14 sm:py-20">
                {done ? (
                    <Confirmation
                        date={date}
                        slot={slot}
                        email={form.email}
                        name={form.name}
                        onHome={() => navigate("/")}
                    />
                ) : (
                    <div className="grid lg:grid-cols-12 gap-12 items-start">
                        <aside className="lg:col-span-5">
                            <span className="eyebrow-pink">Diagnostic offert</span>
                            <h1 className="display-1 mt-4">
                                30 minutes pour <span className="text-brand-500">y voir clair</span>.
                            </h1>
                            <p className="body-lg mt-6">
                                Un appel en visio avec Peggy ou Didier pour analyser la situation de votre
                                enfant et vous donner un plan d'action sur-mesure, basé sur les neurosciences,
                                le stoïcisme et le Système Familial Haute Performance™.
                            </p>

                            <ul className="mt-10 space-y-3.5">
                                {[
                                    "Analyse complète du profil neuro-éducatif",
                                    "Identification des blocages (concentration, écrans, émotions)",
                                    "Plan d'action structuré, applicable immédiatement",
                                    "Sans engagement, 100% confidentiel",
                                ].map((b) => (
                                    <li key={b} className="flex items-start gap-3 text-ink-800">
                                        <CheckCircle2 className="h-5 w-5 text-brand-500 mt-0.5 flex-none" strokeWidth={2} />
                                        <span className="text-sm sm:text-base">{b}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-10 grid grid-cols-2 gap-3">
                                <div className="soft-card flex items-center gap-3 px-4 py-4">
                                    <Video className="h-4 w-4 text-brand-600" strokeWidth={2} />
                                    <span className="text-xs sm:text-sm font-medium text-ink-700">Visio · 1h</span>
                                </div>
                                <div className="soft-card flex items-center gap-3 px-4 py-4">
                                    <ShieldCheck className="h-4 w-4 text-brand-600" strokeWidth={2} />
                                    <span className="text-xs sm:text-sm font-medium text-ink-700">Sans engagement</span>
                                </div>
                            </div>
                        </aside>

                        <form
                            onSubmit={handleSubmit}
                            data-testid="diagnostic-form"
                            className="lg:col-span-7 soft-card p-8 sm:p-10"
                        >
                            <div className="flex items-center gap-3 mb-8">
                                <span className="grid place-items-center h-11 w-11 rounded-2xl bg-brand-500 text-white">
                                    <CalendarCheck className="h-5 w-5" strokeWidth={2} />
                                </span>
                                <div>
                                    <p className="font-display font-semibold text-xl text-ink-900 leading-none">
                                        Réservez votre créneau
                                    </p>
                                    <p className="text-xs uppercase tracking-wider text-ink-500 mt-1.5">
                                        Choisissez une date et un horaire
                                    </p>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-8">
                                <div>
                                    <Label className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                        Date
                                    </Label>
                                    <div className="mt-3 rounded-2xl border border-ink-200 bg-white p-3">
                                        <Calendar
                                            mode="single"
                                            selected={date}
                                            onSelect={setDate}
                                            locale={fr}
                                            weekStartsOn={1}
                                            disabled={(d) =>
                                                d < new Date(new Date().setHours(0, 0, 0, 0)) ||
                                                d.getDay() === 0
                                            }
                                            data-testid="diagnostic-calendar"
                                        />
                                    </div>
                                    <div className="mt-5">
                                        <Label className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                            Créneau
                                        </Label>
                                        <div className="mt-3 flex flex-wrap gap-2" data-testid="diagnostic-slots">
                                            {SLOTS.map((s) => (
                                                <button
                                                    type="button"
                                                    key={s}
                                                    data-testid={`slot-${s}`}
                                                    onClick={() => setSlot(s)}
                                                    className={`text-sm font-medium px-4 py-2 rounded-full border-2 transition-all ${
                                                        slot === s
                                                            ? "bg-brand-500 text-white border-brand-500"
                                                            : "bg-white text-ink-700 border-ink-200 hover:border-brand-300"
                                                    }`}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-5">
                                    <div>
                                        <Label htmlFor="d-name" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                            Votre prénom
                                        </Label>
                                        <Input
                                            id="d-name"
                                            data-testid="diagnostic-name-input"
                                            required
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                                            placeholder="Alex"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="d-email" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                            Email
                                        </Label>
                                        <Input
                                            id="d-email"
                                            data-testid="diagnostic-email-input"
                                            required
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                                            className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                                            placeholder="alex@exemple.fr"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="d-phone" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                            Téléphone <span className="lowercase tracking-normal text-ink-400">(optionnel)</span>
                                        </Label>
                                        <Input
                                            id="d-phone"
                                            data-testid="diagnostic-phone-input"
                                            value={form.phone}
                                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                                            placeholder="+33 6 ..."
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="d-age" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                            Âge de l'enfant
                                        </Label>
                                        <Input
                                            id="d-age"
                                            data-testid="diagnostic-age-input"
                                            value={form.child_age}
                                            onChange={(e) => setForm({ ...form, child_age: e.target.value })}
                                            className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300"
                                            placeholder="9 ans"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="d-message" className="text-xs font-semibold uppercase tracking-wider text-ink-600">
                                            Votre situation
                                        </Label>
                                        <Textarea
                                            id="d-message"
                                            data-testid="diagnostic-message-input"
                                            rows={4}
                                            value={form.message}
                                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                                            className="mt-2 rounded-xl bg-white border-ink-200 focus-visible:ring-brand-300 resize-none"
                                            placeholder="Problématiques rencontrées..."
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        data-testid="diagnostic-submit"
                                        className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi...
                                            </>
                                        ) : (
                                            <>
                                                Confirmer ma demande
                                                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.5} />
                                            </>
                                        )}
                                    </button>
                                    {date && slot && (
                                        <p
                                            data-testid="diagnostic-summary"
                                            className="text-xs text-ink-500 text-center"
                                        >
                                            Rendez-vous le{" "}
                                            <span className="font-semibold text-ink-900">
                                                {format(date, "EEEE d MMMM yyyy", { locale: fr })}
                                            </span>{" "}
                                            à <span className="font-semibold text-ink-900">{slot}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
}

function Confirmation({ date, slot, email, name, onHome }) {
    return (
        <div data-testid="diagnostic-confirmation" className="max-w-xl mx-auto text-center animate-fade-up">
            <span className="grid place-items-center h-16 w-16 rounded-full bg-brand-500 text-white mx-auto">
                <CheckCircle2 className="h-8 w-8" strokeWidth={2} />
            </span>
            <h1 className="display-1 mt-8">
                Merci {name} <span className="text-brand-500">!</span>
            </h1>
            <p className="body-lg mt-6">
                Votre demande de diagnostic est bien enregistrée.
                {date && slot && (
                    <>
                        {" "}
                        Nous vous proposons le{" "}
                        <strong className="text-ink-900">
                            {format(date, "EEEE d MMMM", { locale: fr })} à {slot}
                        </strong>
                        .
                    </>
                )}{" "}
                Nous revenons vers vous sous <strong className="text-ink-900">48h</strong> avec le lien de
                visio.
            </p>
            <p className="mt-4 text-sm text-ink-500">
                Un email récapitulatif a été envoyé à <strong>{email}</strong>.
            </p>
            <button onClick={onHome} data-testid="diagnostic-return-home" className="btn-primary mt-10">
                Retour à l'accueil
            </button>
        </div>
    );
}
